import { useDynamicConfig, useFeatureGate } from "@statsig/react-bindings";
import { secondsToMilliseconds } from "date-fns";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import { isWhiteboardModeAtom } from "../../session/messages/state";
import { useSendWhiteboardSnapshotCallback } from "../../session/messages/useSendWhiteboardSnapshotCallback";
import {
  dynamicConfig,
  whiteboardEnabledGate,
  whiteboardEventualConsistencyGate,
} from "../../statsig/gates";
import { UserType } from "../../types";
import { providerShortIdAtom } from "../state";

/**
 * This interval shouldn't be too short as the whiteboard snapshot can be large.
 */
const DEFAULT_INTERVAL_MS = secondsToMilliseconds(60);

/**
 * Periodically sends the provider's whiteboard snapshots to all participants,
 * aiming for eventual consistency with the provider's whiteboard state.
 */
export function usePeriodicallySendWhiteboardSnapshots() {
  const sendWhiteboardSnapshot = useSendWhiteboardSnapshotCallback();
  const getProviderShortId = useAtomCallback(
    useCallback((get) => get(providerShortIdAtom), []),
  );
  const isWhiteboardMode = useAtomValue(isWhiteboardModeAtom).value;
  const { value: isWhiteboardingEnabled } = useFeatureGate(
    whiteboardEnabledGate,
  );
  const { value: isWhiteboardingEventualConsistencyEnabled } = useFeatureGate(
    whiteboardEventualConsistencyGate,
  );
  const isEnabled =
    isWhiteboardingEnabled && isWhiteboardingEventualConsistencyEnabled;
  const dynamic_interval_seconds = useDynamicConfig(dynamicConfig)?.get<number>(
    "whiteboard_eventual_consistency_interval_seconds",
  );
  const interval_ms = dynamic_interval_seconds
    ? secondsToMilliseconds(dynamic_interval_seconds)
    : DEFAULT_INTERVAL_MS;

  useEffect(
    function handleSendingInterval() {
      if (!isWhiteboardMode || !isEnabled) return;

      const interval = setInterval(async () => {
        const providerShortId = getProviderShortId();
        if (!providerShortId) return;
        await sendWhiteboardSnapshot({
          entityId: providerShortId,
          entityType: UserType.PROVIDER,
        });
      }, interval_ms);

      return () => {
        clearInterval(interval);
      };
    },
    [
      interval_ms,
      isEnabled,
      isWhiteboardMode,
      getProviderShortId,
      sendWhiteboardSnapshot,
    ],
  );
}
